import {useUserStore} from "~/store/userStore.js";
import {useLastVisitedProducts} from "~/store/lastVisitedProducts.js";
import {fetchApiWithRedirects} from "~/utils/apiFetch.ts";
import {authHeaders} from "~/composables/apiAuth.js";

const buildFetchOptions = (opts, headers, method, timeout, retry, retryDelay) => ({
    query: opts.query,
    body: opts.body,
    method,
    timeout,
    retry: 0,
    retryDelay,
    ...opts,
    headers,
})

export const useCustomFetch = async (url, opts = {}, lazy = false) => {
    const lastVisitedProducts = useLastVisitedProducts();
    const config = useRuntimeConfig()

    if (!config || !config.public) {
        throw new Error('Runtime config is not available');
    }

    useUserStore()

    let headers = authHeaders({
        ...opts?.headers,
    })

    const isFormData = typeof FormData !== 'undefined' && opts.body instanceof FormData
    if (isFormData) {
        delete headers['Content-Type']
        delete headers['content-type']
    }

    headers = {
        ...headers,
        referer: config.public.baseURL,
        lastVisitedProducts: JSON.stringify(lastVisitedProducts.products),
        'Accept-Language': config.public.locale,
    }

    const method = String(opts.method ?? 'GET').toUpperCase()
    const isGet = method === 'GET'
    const timeout = typeof opts.timeout === 'number'
        ? opts.timeout
        : (isFormData ? 120000 : (isGet ? 60000 : 20000))
    const retry = typeof opts.retry === 'number' ? opts.retry : (isGet ? 1 : 0)
    const retryDelay = typeof opts.retryDelay === 'number' ? opts.retryDelay : 250
    const fetchOpts = buildFetchOptions(opts, headers, method, timeout, retry, retryDelay)

    const runFetch = async () => {
        let lastError

        for (let attempt = 0; attempt <= retry; attempt++) {
            try {
                return await fetchApiWithRedirects(url, fetchOpts, 0, config.public)
            } catch (error) {
                lastError = error

                if (attempt < retry) {
                    await new Promise(resolve => setTimeout(resolve, retryDelay))
                    continue
                }

                throw error
            }
        }

        throw lastError
    }

    if (lazy) {
        return runFetch()
    }

    const data = ref(null)
    const error = ref(null)

    try {
        data.value = await runFetch()
    } catch (err) {
        error.value = err
    }

    return {data, error}
}
